/**
 * imageFiles.js — Carousel Composer photo intake (browser-only at call time).
 *
 * Files are read in the browser and never uploaded: each becomes an object URL
 * for the original plus a 480px JPEG thumbnail, with the filename parsed for
 * family / capture sequence / variant.
 */
import { bySequence, parseName } from './library.js';

export function isImageFile(f) {
  return /^image\//.test(f.type);
}

export function readImageFile(file, uid) {
  return new Promise((res, rej) => {
    const url = URL.createObjectURL(file);
    const im = new Image();
    im.decoding = 'async';
    im.onload = () => {
      const tw = 480, th = Math.max(1, Math.round(tw * im.naturalHeight / im.naturalWidth));
      const c = document.createElement('canvas');
      c.width = tw;
      c.height = th;
      c.getContext('2d').drawImage(im, 0, 0, tw, th);
      const meta = parseName(file.name);
      const rec = {
        id: uid(), name: file.name, url, thumb: '', w: im.naturalWidth, h: im.naturalHeight,
        fam: meta.fam, seq: meta.seq, seqStr: meta.seqStr, variant: meta.variant, source: 'local',
      };
      c.toBlob((b) => {
        rec.thumb = b ? URL.createObjectURL(b) : c.toDataURL('image/jpeg', 0.72);
        res(rec);
      }, 'image/jpeg', 0.82);
    };
    im.onerror = () => { URL.revokeObjectURL(url); rej(new Error('decode')); };
    im.src = url;
  });
}

// Reads the image files among `files` with a small pool of decodes in flight.
// Unreadable files are skipped. onProgress(done, total, loadedSoFar) fires per file.
// Resolves the new records sorted by bySequence; merge with the existing library
// and re-sort: [...library, ...images].sort(bySequence).
export async function loadImageFiles(files, { uid, onProgress } = {}) {
  const list = Array.from(files).filter(isImageFile);
  if (!list.length) return [];
  list.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  const out = [];
  let done = 0, next = 0;
  if (onProgress) onProgress(0, list.length, out);
  async function worker() {
    while (next < list.length) {
      const f = list[next++];
      try { out.push(await readImageFile(f, uid)); } catch { /* unreadable file */ }
      done++;
      if (onProgress) onProgress(done, list.length, out);
    }
  }
  await Promise.all(Array.from({ length: Math.min(6, list.length) }, worker));
  return out.sort(bySequence);
}
